import { createRouter, createWebHistory } from 'vue-router'
import { useTenantStore } from '@/stores/tenant'
import { useAuthStore } from '@/stores/auth'
import { Permission } from '@/types/Permissions'


const router = createRouter({
    history: createWebHistory(),
    routes: [
        {
            path: '/',
            component: () => import('../layouts/MainLayout.vue'),
            meta: { requiresAuth: true },
            children: [
                {
                    path: 'users',
                    name: 'users',
                    component: () => import('../views/UsersView.vue'),
                    meta: {
                        permission: Permission.USER_READ,
                        title: 'users.view.label.pageTitle',
                        description: 'users.view.label.desc'
                    }
                },
                {
                    path: 'users/:id',
                    name: 'user-profile',
                    component: () => import('../views/UserProfileView.vue'),
                    meta: {
                        permission: Permission.USER_READ,
                        title: 'profile.view.label.pageTitle',
                        description: 'profile.view.label.desc'
                    }
                },

                {
                    path: 'settings',
                    name: 'settings',
                    component: () => import('../views/SettingsView.vue'),
                    meta: {
                        permission: Permission.ACCOUNT_READ,
                        title: 'layout.settings.title',
                        description: 'layout.settings.desc'
                    }
                },
                {
                    path: 'segments',
                    name: 'segments',
                    component: () => import('@/views/SegmentsView.vue'),
                    meta: {
                        requiresAuth: true,
                        permission: 'segment:read',
                        title: 'layout.sidebar.label.segments',
                        description: 'segments.view.label.desc'
                    }
                },
                {
                    path: 'sends',
                    name: 'sends',
                    component: () => import('@/views/SendsView.vue'),
                    meta: {
                        requiresAuth: true,
                        permission: 'announcement:read',
                        title: 'sends.view.label.title',
                        description: 'sends.view.label.desc'
                    }
                },
                // Tam sayfa sihirbaz (kullanıcı kararı: modal değil route).
                {
                    path: 'sends/new',
                    name: 'send-new',
                    component: () => import('@/views/SendWizardView.vue'),
                    meta: {
                        requiresAuth: true,
                        permission: 'announcement:create',
                        title: 'sends.wizard.title'
                    }
                },
                {
                    path: 'sends/:id/edit',
                    name: 'send-edit',
                    component: () => import('@/views/SendWizardView.vue'),
                    meta: {
                        requiresAuth: true,
                        permission: 'announcement:update',
                        title: 'sends.wizard.editTitle'
                    }
                },
                // Eski yollar birleşik Gönderimler'e yönlenir (yer imleri kırılmasın).
                { path: 'announcements', redirect: { path: '/sends', query: { tab: 'ANNOUNCEMENTS' } } },
                {
                    path: 'announcements/:id/stats',
                    name: 'announcement-stats',
                    component: () => import('@/views/AnnouncementStatsView.vue'),
                    meta: {
                        requiresAuth: true,
                        permission: 'announcement:read',
                        title: 'announcement.stats.label.title'
                    }
                },
                { path: 'surveys', redirect: { path: '/sends', query: { tab: 'SURVEYS' } } },
                {
                    path: 'surveys/:id/results',
                    name: 'survey-results',
                    component: () => import('@/views/SurveyResultsView.vue'),
                    meta: {
                        requiresAuth: true,
                        permission: 'survey:read',
                        title: 'survey.results.label.title'
                    }
                },
                {
                    path: 'my-surveys',
                    name: 'my-surveys',
                    component: () => import('@/views/MySurveysView.vue'),
                    meta: {
                        requiresAuth: true,
                        permission: 'survey-response:read',
                        title: 'survey.my.label.title'
                    }
                },
                {
                    path: 'my-surveys/:id',
                    name: 'survey-respond',
                    component: () => import('@/views/SurveyRespondView.vue'),
                    meta: {
                        requiresAuth: true,
                        permission: 'survey-response:read',
                        title: 'survey.respond.label.title'
                    }
                },
                {
                    path: 'goals-admin',
                    name: 'goals-admin',
                    component: () => import('@/views/GoalsAdminView.vue'),
                    meta: {
                        requiresAuth: true,
                        permission: Permission.GOAL_TEMPLATE_READ,
                        title: 'goals.admin.view.title',
                        description: 'goals.admin.view.desc'
                    }
                },
                {
                    path: 'goal-templates',
                    redirect: { name: 'goals-admin', query: { tab: 'TEMPLATES' } }
                },
                {
                    path: 'manager-goals',
                    redirect: { name: 'goals-admin', query: { tab: 'MANAGER_GOALS' } }
                },
                {
                    path: 'my-goals',
                    name: 'my-goals',
                    component: () => import('@/views/MyGoalsView.vue'),
                    meta: {
                        requiresAuth: true,
                        title: 'goals.instance.view.title',
                        description: 'goals.instance.view.subtitle'
                    }
                },
                {
                    path: 'policies',
                    name: 'policies',
                    component: () => import('../views/PoliciesView.vue'),
                    meta: {
                        permission: Permission.POLICY_READ,
                        title: 'policy.view.label.pageTitle',
                        description: 'policy.view.label.desc'
                    }
                },
                {
                    path: 'inbox',
                    name: 'inbox',
                    component: () => import('@/views/InboxView.vue'),
                    props: { mode: 'NOTIFICATIONS' },
                    meta: {
                        permission: Permission.INBOX_READ,
                        title: 'layout.sidebar.label.notifications',
                        description: 'inbox.view.label.desc'
                    }
                },
                {
                    path: 'chat',
                    name: 'chat',
                    component: () => import('@/views/InboxView.vue'),
                    props: { mode: 'MESSAGES' },
                    meta: {
                        // Aynı yetki: Canlı Sohbet de INBOX_READ ile korunur.
                        permission: Permission.INBOX_READ,
                        title: 'layout.sidebar.label.chat',
                        description: 'inbox.view.label.desc'
                    }
                },
                { path: 'notifications', redirect: { path: '/sends', query: { tab: 'TEMPLATES' } } },
                {
                    path: 'reports',
                    name: 'reports',
                    component: () => import('@/views/ReportsView.vue'),
                    meta: {
                        title: 'report.list.title',
                        description: 'report.list.subtitle'
                    }
                },

                {
                    path: 'requests',
                    name: 'requests',
                    component: () => import('@/views/RequestsView.vue'),
                    meta: {
                        permission: Permission.REQUEST_READ,
                        title: 'requests.view.label.title',
                        description: 'requests.view.label.desc'
                    }
                },
                {
                    path: 'profile',
                    name: 'profile',
                    component: () => import('../views/ProfileView.vue'),
                    meta: {
                        permission: Permission.PROFILE_READ,
                        title: 'layout.sidebar.label.profile',
                        description: 'profile.view.label.desc'
                    }
                },
                {
                    path: 'localization',
                    name: 'localization',
                    component: () => import('../views/LocalizationView.vue'),
                    meta: {
                        permission: Permission.I18N_READ,
                        title: 'layout.sidebar.label.localization',
                        description: 'i18n.view.label.desc'
                    }
                },
                {
                    path: 'system/settings',
                    name: 'system-settings',
                    component: () => import('../views/SystemSettingsView.vue'),
                    meta: {
                        permission: Permission.SYSTEM_SETTINGS_MANAGE,
                        title: 'system.view.label.pageTitle',
                        description: 'system.view.label.desc'
                    }
                },
                {
                    path: 'activities',
                    name: 'activities',
                    component: () => import('../views/AgencyActivitiesView.vue'),
                    meta: {
                        permission: Permission.AGENCY_ACTIVITY_READ,
                        title: 'agencyActivities.view.label.title',
                        description: 'agencyActivities.view.label.description'
                    }
                },
                {
                    path: 'violations',
                    name: 'violations',
                    component: () => import('../views/ViolationsView.vue'),
                    meta: {
                        permission: Permission.VIOLATION_READ,
                        title: 'violations.view.label.title',
                        description: 'violations.view.label.description'
                    }
                },
                {
                    path: 'community-rules',
                    name: 'community-rules',
                    component: () => import('../views/CommunityRulesView.vue'),
                    meta: {
                        permission: Permission.COMMUNITY_RULE_READ,
                        title: 'communityRules.view.label.title',
                        description: 'communityRules.view.label.description'
                    }
                },
                {
                    path: 'site',
                    redirect: '/site/creators'
                },
                {
                    path: 'site/creators',
                    name: 'site-creators',
                    component: () => import('../views/SiteView.vue'),
                    meta: {
                        permission: Permission.SITE_READ,
                        title: 'site.view.label.title',
                        description: 'site.view.label.description'
                    }
                },
                {
                    path: 'site/content',
                    name: 'site-content',
                    component: () => import('../views/SiteContentView.vue'),
                    meta: {
                        permission: Permission.SITE_READ,
                        title: 'site.content.title',
                        description: 'site.content.hint'
                    }
                },
                {
                    // Site başvuruları — eskiden Yayıncı Adayları'nda sekmeydi. Uçlar creator-lead:read ister.
                    path: 'site/applications',
                    name: 'site-applications',
                    component: () => import('../views/SiteApplicationsView.vue'),
                    meta: {
                        permission: Permission.CREATOR_LEAD_READ,
                        title: 'siteApplications.title'
                    }
                },
                {
                    path: 'site/settings',
                    name: 'site-settings',
                    component: () => import('../views/SiteSettingsView.vue'),
                    meta: {
                        permission: Permission.SITE_READ,
                        title: 'site.settings.title',
                        description: 'site.settings.hint'
                    }
                },
                {
                    path: 'guides',
                    name: 'guides',
                    component: () => import('../views/GuidesView.vue'),
                    meta: {
                        permission: Permission.GUIDE_READ,
                        title: 'guides.title',
                        description: 'guides.hint'
                    }
                },
                // ── İş Ortağı modülü (modül anahtarı: partners) ──
                {
                    path: 'partner/find',
                    name: 'partner-find',
                    component: () => import('../views/partner/PartnerFindView.vue'),
                    meta: { permission: 'partner:portal', title: 'partners.find.title', description: 'partners.find.desc' }
                },
                {
                    path: 'partner/creators',
                    name: 'partner-creators',
                    component: () => import('../views/partner/PartnerCreatorsView.vue'),
                    meta: { permission: 'partner:portal', title: 'partners.creators.title', description: 'partners.creators.desc' }
                },
                {
                    path: 'partner/earnings',
                    name: 'partner-earnings',
                    component: () => import('../views/partner/PartnerEarningsView.vue'),
                    meta: { permission: 'partner:portal', title: 'partners.earnings.title', description: 'partners.earnings.desc' }
                },
                {
                    // Modül bu ajansta kapalıyken iş ortağının göreceği sade ekran (MODULE_ROUTES dışında).
                    path: 'partner-off',
                    name: 'partner-off',
                    component: () => import('../views/partner/PartnerModuleOffView.vue'),
                    meta: { title: 'partners.off.title' }
                },
                {
                    path: 'partners',
                    name: 'partners',
                    component: () => import('../views/partner/PartnersView.vue'),
                    meta: { permission: 'partner:manage', title: 'partners.admin.title', description: 'partners.admin.desc' }
                },
                {
                    path: 'partners/plans/:id',
                    name: 'partner-plan',
                    component: () => import('../views/partner/PartnerPlanEditorView.vue'),
                    meta: { permission: 'partner:manage', title: 'partners.plan.title' }
                },
                {
                    path: 'partners/:id',
                    name: 'partner-detail',
                    component: () => import('../views/partner/PartnersView.vue'),
                    meta: { permission: 'partner:manage', title: 'partners.detail.title' }
                },
                {
                    path: 'equipments',
                    name: 'equipments',
                    component: () => import('../views/EquipmentsView.vue'),
                    meta: {
                        permission: Permission.EQUIPMENT_MANAGE,
                        title: 'equipment.page.title',
                        description: 'equipment.page.description'
                    }
                },
                {
                    path: 'earnings-distribution',
                    name: 'earnings-distribution',
                    component: () => import('@/views/EarningsDistributionView.vue'),
                    meta: {
                        requiresAuth: true,
                        permission: Permission.EARNINGS_DISTRIBUTION_MANAGE,
                        title: 'earningsDistribution.view.title',
                        description: 'earningsDistribution.view.title'
                    }
                },
                {
                    path: 'incentives',
                    name: 'incentives',
                    component: () => import('@/views/IncentivesView.vue'),
                    meta: {
                        requiresAuth: true,
                        permission: 'incentive:read',
                        title: 'incentives.view.title',
                        description: 'incentives.view.subtitle'
                    }
                },
                // Eski adres — yer imleri/paylaşılmış linkler kırılmasın
                {
                    path: 'incentive-push',
                    redirect: '/incentives'
                },
                {
                    path: 'scrape/manual-trigger',
                    name: 'scrape-manual-trigger',
                    component: () => import('@/views/ScrapeManualTriggerView.vue'),
                    meta: {
                        permission: Permission.CREATOR_SNAPSHOT_CREATE,
                        title: 'scrape.trigger.view.title',
                        description: 'scrape.trigger.view.subtitle'
                    }
                },
                {
                    path: 'creator-leads',
                    name: 'creator-leads',
                    component: () => import('@/views/CreatorLeadsView.vue'),
                    meta: {
                        permission: Permission.CREATOR_LEAD_READ,
                        title: 'creatorLead.view.title',
                        description: 'creatorLead.view.subtitle'
                    }
                },
                {
                    path: 'creator-leads/ai-analyses',
                    name: 'CreatorLeadAIAnalyses',
                    component: () => import('@/views/CreatorLeadAIAnalysesView.vue'),
                    meta: {
                        permission: Permission.CREATOR_LEAD_READ,
                        title: 'creatorLead.ai.history.title',
                        description: 'creatorLead.ai.history.subtitle'
                    }
                },

                {
                    path: 'panel',
                    name: 'panel',
                    component: () => import('../views/PanelView.vue'),
                    meta: {
                        permission: Permission.PANEL_READ,
                        title: 'panel.title',
                        description: 'panel.description'
                    }
                },
                {
                    path: '',
                    name: 'home',
                    redirect: _to => {
                        const authStore = useAuthStore()
                        const role = authStore.user?.role
                        if (role === 'ADMIN' || role === 'MANAGER') return { name: 'panel' }
                        // İş ortağı girişte "Yayıncı bul"a düşer; modül kapalıysa sade kapalı ekranına.
                        if (role === 'PARTNER') {
                            return useTenantStore().moduleEnabled('partners') ? { name: 'partner-find' } : { name: 'partner-off' }
                        }
                        return { name: 'profile' }
                    }
                }
            ]
        },
        {
            path: '/login',
            name: 'login',
            component: () => import('../views/LoginView.vue')
        },
        {
            // Public duyuru sayfası — auth İSTEMEZ (login/forgot-password istisna kalıbı):
            // requiresAuth meta'sı yok → guard'daki auth ve permission blokları atlanır.
            path: '/a/:token',
            name: 'public-announcement',
            component: () => import('../views/PublicAnnouncementView.vue')
        },
        {
            path: '/403',
            name: 'forbidden',
            component: () => import('../views/ForbiddenView.vue')
        },
        {
            path: '/forgot-password',
            name: 'forgot-password',
            component: () => import('../views/ForgotPasswordView.vue')
        },
        {
            path: '/set-password',
            name: 'set-password',
            component: () => import('../views/SetPasswordView.vue')
        },
        {
            path: '/complete-profile',
            name: 'complete-profile',
            component: () => import('../views/CompleteProfileView.vue'),
            meta: { requiresAuth: true }
        },
    ]
})

let sessionRestorePromise: Promise<void> | null = null


// First URL path segment → TCM module key (mirrors useNavMenu's module keys).
const MODULE_ROUTES: Record<string, string> = {
    'goals-admin': 'goals',
    'segments': 'segments',
    'activities': 'activities',
    'creator-leads': 'leads',
    'chat': 'chat',
    'equipments': 'equipment',
    'incentives': 'incentive',
    'surveys': 'surveys',
    'announcements': 'announcements',
    'site': 'website',
    'guides': 'guides',
    'partner': 'partners',
    'partners': 'partners',
}

router.beforeEach(async (to, _from, next) => {
    const authStore = useAuthStore()

    // Check for requiresAuth
    if (to.matched.some(record => record.meta.requiresAuth)) {
        if (!authStore.isAuthenticated) {
            // Singleton promise — only one /me call at a time
            if (!sessionRestorePromise) {
                sessionRestorePromise = authStore.restoreSession(true).finally(() => {
                    sessionRestorePromise = null
                })
            }
            await sessionRestorePromise
            if (!authStore.isAuthenticated) {
                return next('/login')
            }
        }

        // İş ortağının profil ekranı yok: /profile ve /complete-profile evine döner.
        if (authStore.user?.role === 'PARTNER' && (to.name === 'profile' || to.name === 'complete-profile')) {
            return next({ name: 'home' })
        }

        // First login check: redirect to complete-profile if needed
        // İş ortağı yayıncı profil formunu doldurmaz.
        if (authStore.user?.firstLogin && authStore.user?.role !== 'PARTNER' && to.name !== 'complete-profile') {
            return next('/complete-profile')
        }
    }

    // Check for permission requirements
    if (to.meta.permission) {
        const requiredPermission = to.meta.permission as string
        if (!authStore.hasPermission(requiredPermission)) {
            return next('/403')
        }
    }

    // TCM module gate (defense-in-depth with the menu; visibility = module ∧ permission).
    // Best-effort on direct URL entry: modules load right after the layout mounts.
    const seg = to.path.split('/')[1]
    if (seg && MODULE_ROUTES[seg] && !useTenantStore().moduleEnabled(MODULE_ROUTES[seg])) {
        // İş ortağı kapalı modülde 403 yerine "Bu bölüm bu ajansta kapalı" ekranını görür.
        if (authStore.user?.role === 'PARTNER') return next('/partner-off')
        return next('/403')
    }

    next()
})

// Stale chunk recovery: deploy sonrası tarayıcıda eski index.html açıkken
// kullanıcı route değiştirdiğinde lazy chunk hash'i artık server'da yok →
// "Importing a module script failed" / "Failed to fetch dynamically imported
// module". Bu hatayı yakalayıp tam sayfa reload yap → yeni index.html + yeni
// chunk hash'leri çekilir, kullanıcı CMD+R'a basmak zorunda kalmaz.
const CHUNK_FAIL_RE = /Loading chunk .* failed|Failed to fetch dynamically imported module|Importing a module script failed/i

router.onError((error, to) => {
    const message = error instanceof Error ? error.message : String(error)
    if (CHUNK_FAIL_RE.test(message)) {
        const target = to?.fullPath ?? window.location.pathname + window.location.search
        // Reload bayrağı ekle: aynı session'da tekrar tetiklenirse infinite reload'dan kaç
        const RELOAD_FLAG = 'tap:chunk-reload'
        if (sessionStorage.getItem(RELOAD_FLAG) !== '1') {
            sessionStorage.setItem(RELOAD_FLAG, '1')
            window.location.replace(target)
        }
    }
})

router.afterEach(() => {
    sessionStorage.removeItem('tap:chunk-reload')
})

export default router
